import {
  FileUploadProvider,
  useFileUploadStore,
} from '@common/uploads/uploader/file-upload-provider';
import {FileUploadStoreOptions} from '@common/uploads/uploader/file-upload-store';
import {getActiveWorkspaceId} from '@common/workspace/active-workspace-id';
import {useEffect} from 'react';
import {Outlet} from 'react-router';
import {UploadQueue} from '@app/drive/uploading/upload-queue';

const uploadStoreOptions: FileUploadStoreOptions = {
  modifyUploadedFile: uploadedFile => {
    const workspaceId = getActiveWorkspaceId();
    uploadedFile.fingerprint = `${uploadedFile.fingerprint}-w-${workspaceId}`;
    return uploadedFile;
  },
};

/**
 * Hosts the file-upload engine and progress queue ABOVE the router so uploads
 * keep running (and stay visible) when the user navigates between pages such as
 * the drive and billing. Without this, leaving the drive layout would destroy
 * the upload store and abort in-flight uploads.
 */
export function PersistentUploadLayout() {
  return (
    <FileUploadProvider options={uploadStoreOptions}>
      <Outlet />
      <UploadQueue />
      <UnloadWarning />
    </FileUploadProvider>
  );
}

/**
 * Warn before a full page unload (reload / tab close) while uploads are active,
 * since a hard navigation cannot be resumed.
 */
function UnloadWarning() {
  const activeUploadsCount = useFileUploadStore(s => s.activeUploadsCount);

  useEffect(() => {
    if (!activeUploadsCount) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [activeUploadsCount]);

  return null;
}
